<!--

Note to developers: This is not an "official" Design System component yet, as
it doesn't exist in Figma. However, it will need to be added in the future.
For now, this serves as a starting point. This component is currently used on
the Companies page, so any future changes must maintain compatibility.

USAGE NOTES:
- Set `minChars` to control how many characters must be typed before showing
  suggestions
- `bind:show` to externally control dropdown visibility
- `bind:isTyping` to track when user is actively entering text
- Provide `handleSelect` to process selected options
- Provide `onEnterKeyPress` to handle Enter key press without selection
- Provide custom `handleBlur` only if you need to override default behavior

-->

<style lang="scss">
  @use "../../../assets/css/design-system/mixins";
  @use "../../../assets/css/design-system/colors";
  @use "../../../assets/css/design-system/utils";

  .autocomplete-wrapper {
    width: 100%;
    position: relative;
    @include mixins.reset-button;
  }

  .dropdown-inner {
    overflow-y: auto;
    &.restrict-height {
      max-height: utils.in_rem(200px);
    }
  }
</style>

<script>
  import { onDestroy, tick } from "svelte";
  import { makeEscapeAction, makeWindowClick } from "../../../utils/escapable";
  import FloatingUI from "../../utils/FloatingUI.svelte";
  import ListItem from "../Dropdown/ListItem.svelte";
  import { ObserverStore } from "../Dropdown/stores";
  import TextInput from "./TextInput.svelte";

  /** Component props with defaults */
  export let value = "";
  export let suggestions = [];
  export let minChars = 3;
  export let autoFocus = false;
  export let show = false; // Controls dropdown visibility
  export let isTyping = false; // Tracks if user is typing
  export let isLoading = false; // Indicates when suggestions are being loaded
  export let restrictHeight = false; // Whether to restrict height of dropdown
  export let handleSelect; // Handler for when an option is selected
  export let handleFocus = () => {};
  export let handleBlur; // Optional custom blur handler
  export let onEnterKeyPress = null; // Handler for Enter key pressed without selection
  export let onInput = null; // Handler for input value changes

  // Internal state
  let textInputEl;
  let wrapperEl;
  let floatingEl;
  let filtered = [];
  let selectedIndex = -1;
  let windowClickHandler = null;

  // Observe wrapper element to match dropdown width
  const observerStore = ObserverStore();
  $: if (wrapperEl) {
    $observerStore = wrapperEl;
  }
  $: style = `width: ${$observerStore.width}px;`;

  /** Close the dropdown and reset selection */
  const close = () => {
    show = false;
    selectedIndex = -1;
  };

  // Handle clicks outside component with manual event handling
  // This avoids directly using removeEventListener which triggers linter errors
  const handleWindowClick = (e) => {
    if (windowClickHandler) {
      windowClickHandler(e);
    }
  };

  // Update the window click handler when necessary
  $: {
    if (wrapperEl) {
      windowClickHandler = makeWindowClick(close, wrapperEl, floatingEl);
    }
  }

  onDestroy(() => {
    // Allow cleanup to be handled by Svelte
    windowClickHandler = null;
  });

  const escape = makeEscapeAction(close);

  /** Handles input changes, shows suggestions if appropriate */
  const handleInput = () => {
    isTyping = true;

    if (onInput) {
      onInput(value);
    }

    if (value?.length >= minChars) {
      // Use tick to ensure we have latest filtered results
      tick().then(() => {
        show = filtered.length > 0;
      });
    } else {
      show = false;
    }
  };

  /** Select a suggestion and update value */
  const select = (suggestion) => {
    value = suggestion.label;
    isTyping = false;

    if (handleSelect) {
      handleSelect(suggestion);
    }

    close();
  };

  /** Keyboard navigation handler */
  const keydown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (show && filtered.length && selectedIndex >= 0) {
        // If dropdown is visible and an item is selected, use that item
        select(filtered[selectedIndex]);
      } else if (onEnterKeyPress) {
        // Otherwise call the custom Enter handler if provided
        onEnterKeyPress(value);
      }
      return;
    }

    if (show && filtered.length) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          selectedIndex = Math.min(selectedIndex + 1, filtered.length - 1);
          break;
        case "ArrowUp":
          e.preventDefault();
          selectedIndex = Math.max(selectedIndex - 1, -1);
          break;
        case "Escape":
          e.preventDefault();
          close();
          break;
        default:
          break;
      }
    }
  };

  /** Handle blur events - either with custom handler or default behavior */
  const onBlur = () => {
    if (handleBlur) {
      handleBlur();
    } else {
      show = false;
    }
  };

  /** Reactive filtering of suggestions based on input value */
  $: {
    if (value?.length >= minChars) {
      filtered = suggestions
        .filter((s) => s.label.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 10);

      if (filtered.length > 0 && isTyping) {
        show = true;
      } else if (filtered.length === 0) {
        show = false;
      }
    } else {
      filtered = [];
      show = false;
    }
  }
</script>

<svelte:window on:click={handleWindowClick} />

<div
  class="autocomplete-wrapper"
  bind:this={wrapperEl}
  on:click|stopPropagation
  on:keydown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  }}
  tabindex="0"
  role="combobox"
  aria-expanded={show}
  aria-haspopup="listbox"
  aria-controls={show && filtered.length > 0
    ? "autocomplete-dropdown"
    : undefined}
>
  <TextInput
    bind:this={textInputEl}
    bind:value
    {autoFocus}
    {isLoading}
    on:input={handleInput}
    on:keydown={keydown}
    on:focus={handleFocus}
    on:blur={onBlur}
    {...$$restProps}
  />

  {#if show && filtered.length > 0 && wrapperEl}
    <FloatingUI
      referenceEl={wrapperEl}
      bind:floatingEl
      {escape}
      {style}
      class="dropdown"
      placement="bottom"
      id="autocomplete-dropdown"
      role="listbox"
    >
      <div class="dropdown-inner" class:restrict-height={restrictHeight}>
        {#each filtered as suggestion, i}
          {#if $$slots.item}
            <slot
              name="item"
              {suggestion}
              index={i}
              selected={i === selectedIndex}
              {select}
            />
          {:else}
            <ListItem
              selected={i === selectedIndex}
              onclick={() => select(suggestion)}
            >
              {suggestion.label}
            </ListItem>
          {/if}
        {/each}
      </div>
    </FloatingUI>
  {/if}
</div>
